import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    DataGrid,
    GridActionsCellItem,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import SettingsIcon from '@mui/icons-material/Settings';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';

import EventFormModal from '../components/EventFormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import EventDetailsModal from '../components/EventDetailsModal';
import EventCategoriesConfigModal from '../components/EventCategoriesConfigModal';
import { 
    useListEvents, 
    useDeleteEvent, 
    useListOrganizations
} from '../services/appService';
import { useAuth } from '../context/AuthContext';

const formatTime = (timeInput) => {
    if (!timeInput) return 'N/A';
    // Basic time format assumption (HH:MM or HH:MM:SS)
    // More robust parsing might be needed depending on DB storage
    // return timeInput.slice(0, 5); // Show HH:MM
    // Display the full string from the DB (e.g., "Um 19:09")
    return timeInput;
}

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function EventsPage() {
    const { appId } = useParams();
    const { isRedaktion, isOrgAdmin } = useAuth();
    const [events, setEvents] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOrgs, setLoadingOrgs] = useState(true);
    const [error, setError] = useState('');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null); // null for create, event object for edit
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    // States for details modal
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedEventForDetails, setSelectedEventForDetails] = useState(null);
    const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);

    const listEventsHook = useListEvents();
    const deleteEventHook = useDeleteEvent();
    const listOrganizationsHook = useListOrganizations();
    // getEventDetailsHook is used within the form modal for editing

    const fetchEvents = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await listEventsHook(appId);
            setEvents(data || []);
        } catch (err) {
            console.error("Error fetching events:", err);
            setError(err.message || 'Failed to load events.');
        } finally {
            setLoading(false);
        }
    }, [appId, listEventsHook]);

    // Fetch organizations
    const fetchOrganizations = useCallback(async () => {
        if (!appId) return;
        setLoadingOrgs(true);
        // Don't reset main error, or handle org error separately
        try {
            const orgData = await listOrganizationsHook(appId);
            setOrganizations(orgData || []);
        } catch (err) {
            console.error("Error fetching organizations for grid:", err);
            // Optionally set a specific error state for orgs
            // setError('Failed to load organizations list.'); 
            setOrganizations([]); // Ensure it's an empty array on error
        } finally {
            setLoadingOrgs(false);
        }
    }, [appId, listOrganizationsHook]);

    useEffect(() => {
        fetchEvents();
        fetchOrganizations();
    }, [fetchEvents, fetchOrganizations]);

    const handleOpenCreateModal = () => {
        setEditingEvent(null);
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (event) => {
        // Pass the event summary; the modal will fetch full details
        setEditingEvent(event);
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setEditingEvent(null);
    };

    const handleSaveEvent = () => {
        handleCloseFormModal();
        fetchEvents(); // Refresh list
    };

    const handleOpenConfirmDialog = (eventId) => {
        setEventToDelete(eventId);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setEventToDelete(null);
        setConfirmDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!eventToDelete || !appId) return;
        setLoading(true); // Show loading indicator during delete
        try {
            await deleteEventHook(appId, eventToDelete);
            fetchEvents(); // Refresh list after delete
        } catch (err) {
            console.error("Error deleting event:", err);
            setError(err.message || 'Failed to delete event.');
            setLoading(false); // Turn off loading on error
        } finally {
            handleCloseConfirmDialog();
            // Loading state is turned off by fetchEvents or the catch block
        }
    };

    // Handlers for details modal
    const handleOpenDetailsModal = (event) => {
        setSelectedEventForDetails(event); // Pass the whole event row
        setDetailsModalOpen(true);
    };
    const handleCloseDetailsModal = () => {
        setSelectedEventForDetails(null);
        setDetailsModalOpen(false);
    };

    // Handlers for categories modal
    const handleOpenCategoriesModal = () => {
        setIsCategoriesModalOpen(true);
    };
    const handleCloseCategoriesModal = () => {
        setIsCategoriesModalOpen(false);
    };

    const columns = [
        { 
            field: 'image_url',
            headerName: 'Image',
            width: 80,
            renderCell: (params) => (
                params.value ? 
                <Tooltip title={params.value}>
                    <Avatar 
                        src={params.value} 
                        variant="rounded" 
                        sx={{ width: 40, height: 40, cursor: 'pointer' }}
                        onClick={() => window.open(params.value, '_blank')} // Open image in new tab
                    />
                </Tooltip> : 
                <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}> </Avatar> // Placeholder
            ),
            sortable: false,
            filterable: false,
        },
        { field: 'id', headerName: 'ID', width: 150 },
        { field: 'title', headerName: 'Title', width: 250 },
        { field: 'category', headerName: 'Category', width: 130 },
        {
            field: 'date',
            headerName: 'Date',
            width: 120,
            renderCell: (params) => {
                const dateString = params.value;
                if (!dateString) return 'N/A';
                try {
                    // Create date object assuming UTC based on YYYY-MM-DD format
                    // Splitting avoids potential timezone issues with new Date()
                    const parts = dateString.split('-');
                    if (parts.length !== 3) return 'Invalid Date';
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
                    const day = parseInt(parts[2], 10);
                    const date = new Date(Date.UTC(year, month, day));

                    // Check if the created date is valid
                    if (isNaN(date.getTime())) return 'Invalid Date';
                    
                    // Format to locale specific date string (e.g., MM/DD/YYYY or DD.MM.YYYY)
                    return date.toLocaleDateString(); 
                } catch (e) {
                    console.error("Error parsing/formatting date string:", e, dateString);
                    return 'Invalid Date'; // Fallback
                }
            },
            sortable: true 
        },
        {
            field: 'time',
            headerName: 'Start Time',
            width: 100,
            sortable: false,
        },
        {
            field: 'location',
            headerName: 'Location',
            width: 180,
        },
         {
            field: 'recurrence_rule',
            headerName: 'Repeats',
            width: 180,
             renderCell: (params) => (
                params.value ? (
                    <Tooltip title={params.value}>
                        <Typography variant="body2" noWrap>{params.value}</Typography>
                    </Tooltip>
                ) : (
                    <Typography variant="body2" color="text.secondary">No</Typography>
                )
            ),
            sortable: false,
            filterable: false,
        },
        {
            field: 'organization_id', // Keep field for data access
            headerName: 'Organizer/Org',
            width: 180,
            // Replace valueGetter with renderCell for state dependency
            renderCell: (params) => {
                const orgId = params.value; // Direct access to organization_id

                if (loadingOrgs) {
                    return <Typography variant="caption">Loading Orgs...</Typography>;
                }
                if (!orgId) {
                    return <Typography variant="body2">N/A (Personal)</Typography>;
                }
                
                const organization = organizations.find(org => org.id === orgId);
                return (
                    <Typography variant="body2">
                        {organization ? organization.name : 'N/A (Unknown ID)'}
                    </Typography>
                );
            },
            sortable: false, // Sorting might be complex with lookup, disable for now
            filterable: false, // Filtering also complex
        },
        {
            field: 'is_published',
            headerName: 'Published',
            width: 100,
            type: 'boolean'
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 120, // Adjusted width
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                const actions = [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={() => handleOpenEditModal(row)} // Pass the row data
                        color="inherit"
                    />,
                    // Add View Details button 
                    <GridActionsCellItem
                        icon={<CommentIcon />} // Reuse CommentIcon or choose another like VisibilityIcon
                        label="View Details"
                        onClick={() => handleOpenDetailsModal(row)} // Pass row data
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleOpenConfirmDialog(id)}
                        color="inherit"
                    />,
                ];
                return actions;
            },
        },
    ];

    return (
        <Box sx={{ height: 'calc(100vh - 120px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <Typography variant="h4" gutterBottom>
                    Manage Events
                </Typography>
                <Box>
                     {!isRedaktion() && !isOrgAdmin() && (
                       <Button
                          variant="outlined"
                          startIcon={<SettingsIcon />}
                          onClick={handleOpenCategoriesModal}
                          sx={{ mr: 1 }}
                      >
                          Event Categories
                      </Button>
                     )}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateModal}
                    >
                        Create Event
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

            {loading || loadingOrgs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <DataGrid
                        rows={events}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 10 },
                            },
                            sorting: {
                                sortModel: [{ field: 'date', sort: 'desc' }],
                            },
                             density: 'compact', // Make rows denser to fit more columns
                        }}
                        checkboxSelection={false}
                        disableRowSelectionOnClick
                        slots={{ toolbar: CustomToolbar }}
                        slotProps={{
                             toolbar: { setFilterButtonEl: () => {} },
                        }}
                         sx={{
                            border: 0,
                            '& .MuiDataGrid-main': {
                                height: '100%',
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                height: 'calc(100% - 56px - 56px)', // Adjust for header and toolbar
                                overflowY: 'auto'
                            },
                        }}
                    />
                </Box>
            )}

            <EventFormModal
                open={isFormModalOpen}
                onClose={handleCloseFormModal}
                onSave={handleSaveEvent}
                event={editingEvent}
                appId={appId}
            />

            {/* Render EventDetailsModal */}
            <EventDetailsModal
                open={detailsModalOpen}
                onClose={handleCloseDetailsModal}
                appId={appId}
                eventId={selectedEventForDetails?.id}      // Pass event ID
                eventTitle={selectedEventForDetails?.title} // Pass event title
            />

            {/* Render EventCategoriesConfigModal */}
            <EventCategoriesConfigModal
                open={isCategoriesModalOpen}
                onClose={handleCloseCategoriesModal}
                appId={appId}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleDeleteConfirm}
                title="Delete Event?"
                message={`Are you sure you want to delete event ID: ${eventToDelete}? This action cannot be undone and might affect related comments or attendees.`}
            />
        </Box>
    );
}

export default EventsPage; 